import React, { useState } from 'react';
import { Plus, Edit, Trash2, X, Settings } from 'lucide-react';

import axios, { API } from './apiClient';

// Auto-attach Bearer token for cross-origin auth
axios.interceptors.request.use(config => {
  const token = sessionStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const FieldManager = ({ customFields, customSections, onSave }) => {
  const [showFieldForm, setShowFieldForm] = useState(false);
  const [showSectionForm, setShowSectionForm] = useState(false);
  const [fieldForm, setFieldForm] = useState({ field_name: '', field_label: '', field_type: 'text', section: 'Custom Fields', options: '', applies_to: ['lead', 'prospect', 'customer'], required: false });
  const [sectionForm, setSectionForm] = useState({ section_name: '', applies_to: ['lead', 'prospect', 'customer'] });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'date', label: 'Date' },
    { value: 'select', label: 'Dropdown' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'url', label: 'URL/Website' }
  ];

  const addSection = async () => {
    if (!sectionForm.section_name.trim()) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/admin/custom-sections`, sectionForm, {});
      setSectionForm({ section_name: '', applies_to: ['lead', 'prospect', 'customer'] });
      setShowSectionForm(false);
      onSave();
    } catch (error) { if (process.env.NODE_ENV === 'development') console.error('Error:', error); }
    finally { setIsSubmitting(false); }
  };

  const deleteSection = async (sectionId) => {
    if (!window.confirm('Delete this section? All fields in this section will need to be reassigned.')) return;
    try {
      await axios.delete(`${API}/admin/custom-sections/${sectionId}`, {});
      onSave();
    } catch (error) { if (process.env.NODE_ENV === 'development') console.error('Error:', error); }
  };

  const addField = async () => {
    if (!fieldForm.field_name.trim() || !fieldForm.field_label.trim()) return;
    setIsSubmitting(true);
    try {
      const data = { ...fieldForm, options: fieldForm.options ? fieldForm.options.split(',').map(o => o.trim()) : [] };
      await axios.post(`${API}/admin/custom-fields`, data, {});
      setFieldForm({ field_name: '', field_label: '', field_type: 'text', section: 'Custom Fields', options: '', applies_to: ['lead', 'prospect', 'customer'], required: false });
      setShowFieldForm(false);
      onSave();
    } catch (error) { if (process.env.NODE_ENV === 'development') console.error('Error:', error); }
    finally { setIsSubmitting(false); }
  };

  const deleteField = async (fieldId) => {
    if (!window.confirm('Delete this field? This will not remove data already saved with this field.')) return;
    try {
      await axios.delete(`${API}/admin/custom-fields/${fieldId}`, {});
      onSave();
    } catch (error) { if (process.env.NODE_ENV === 'development') console.error('Error:', error); }
  };

  const toggleAppliesTo = (form, setForm, entity) => {
    const current = form.applies_to || [];
    if (current.includes(entity)) {
      setForm({ ...form, applies_to: current.filter(e => e !== entity) });
    } else {
      setForm({ ...form, applies_to: [...current, entity] });
    }
  };

  // Get all section names (built-in + custom)
  const allSections = ['Custom Fields', ...customSections.map(s => s.section_name)];

  // Group fields by section
  const fieldsBySection = {};
  customFields.forEach(f => {
    const section = f.section || 'Custom Fields';
    if (!fieldsBySection[section]) fieldsBySection[section] = [];
    fieldsBySection[section].push(f);
  });

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
        <Settings className="inline mr-2" size={24} />Field Manager
      </h2>
      <p className="text-slate-600 mb-6">Create custom sections and fields that will appear in Lead, Prospect, and Customer forms.</p>

      {/* Sections */}
      <div className="bg-white border rounded-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Custom Sections</h3>
          <button onClick={() => setShowSectionForm(!showSectionForm)} className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded flex items-center">
            <Plus size={18} className="mr-1" />{showSectionForm ? 'Cancel' : 'Add Section'}
          </button>
        </div>

        {showSectionForm && (
          <div className="bg-purple-50 p-4 rounded-lg mb-4 border border-purple-200">
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Section Name *</label>
                <input type="text" value={sectionForm.section_name} onChange={(e) => setSectionForm({ ...sectionForm, section_name: e.target.value })} placeholder="e.g. Project Details" className="w-full px-3 py-2 border rounded" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Applies To</label>
                <div className="flex items-center space-x-4 mt-2">
                  {['lead', 'prospect', 'customer'].map(entity => (
                    <label key={entity} className="flex items-center space-x-1 cursor-pointer">
                      <input type="checkbox" checked={sectionForm.applies_to.includes(entity)} onChange={() => toggleAppliesTo(sectionForm, setSectionForm, entity)} className="rounded" />
                      <span className="capitalize">{entity}s</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <button onClick={addSection} disabled={isSubmitting} className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded">Save Section</button>
          </div>
        )}

        {customSections.length > 0 ? (
          <div className="space-y-2">
            {customSections.map(section => (
              <div key={section.id} className="flex justify-between items-center p-3 bg-slate-50 rounded">
                <div>
                  <span className="font-medium">{section.section_name}</span>
                  <span className="text-xs text-slate-500 ml-2">({section.applies_to?.join(', ') || 'all'})</span>
                </div>
                <button onClick={() => deleteSection(section.id)} className="text-red-500 hover:text-red-700"><Trash2 size={18} /></button>
              </div>
            ))}
          </div>
        ) : <p className="text-slate-500 text-center py-4">No custom sections yet.</p>}
      </div>

      {/* Fields */}
      <div className="bg-white border rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Custom Fields</h3>
          <button onClick={() => setShowFieldForm(!showFieldForm)} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded flex items-center">
            <Plus size={18} className="mr-1" />{showFieldForm ? 'Cancel' : 'Add Field'}
          </button>
        </div>

        {showFieldForm && (
          <div className="bg-blue-50 p-4 rounded-lg mb-4 border border-blue-200">
            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Field Name * (no spaces)</label>
                <input type="text" value={fieldForm.field_name} onChange={(e) => setFieldForm({ ...fieldForm, field_name: e.target.value.toLowerCase().replace(/\s+/g, '_') })} placeholder="e.g. permit_number" className="w-full px-3 py-2 border rounded" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Field Label *</label>
                <input type="text" value={fieldForm.field_label} onChange={(e) => setFieldForm({ ...fieldForm, field_label: e.target.value })} placeholder="e.g. Permit Number" className="w-full px-3 py-2 border rounded" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Field Type</label>
                <select value={fieldForm.field_type} onChange={(e) => setFieldForm({ ...fieldForm, field_type: e.target.value })} className="w-full px-3 py-2 border rounded">
                  {fieldTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Section</label>
                <select value={fieldForm.section} onChange={(e) => setFieldForm({ ...fieldForm, section: e.target.value })} className="w-full px-3 py-2 border rounded">
                  {allSections.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {fieldForm.field_type === 'select' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Options (comma-separated)</label>
                  <input type="text" value={fieldForm.options} onChange={(e) => setFieldForm({ ...fieldForm, options: e.target.value })} placeholder="Option 1, Option 2, Option 3" className="w-full px-3 py-2 border rounded" />
                </div>
              )}
            </div>
            <div className="flex items-center space-x-6 mb-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" checked={fieldForm.required} onChange={(e) => setFieldForm({ ...fieldForm, required: e.target.checked })} className="rounded" />
                <span>Required Field</span>
              </label>
              <span className="text-sm text-slate-600">Applies to:</span>
              {['lead', 'prospect', 'customer'].map(entity => (
                <label key={entity} className="flex items-center space-x-1 cursor-pointer">
                  <input type="checkbox" checked={fieldForm.applies_to.includes(entity)} onChange={() => toggleAppliesTo(fieldForm, setFieldForm, entity)} className="rounded" />
                  <span className="capitalize">{entity}s</span>
                </label>
              ))}
            </div>
            <button onClick={addField} disabled={isSubmitting} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded">Save Field</button>
          </div>
        )}

        {customFields.length > 0 ? (
          <div className="space-y-4">
            {Object.keys(fieldsBySection).map(section => (
              <div key={section}>
                <h4 className="text-sm font-semibold text-slate-600 mb-2">{section}</h4>
                <div className="space-y-2">
                  {fieldsBySection[section].map(field => (
                    <div key={field.id} className="flex justify-between items-center p-3 bg-slate-50 rounded">
                      <div>
                        <span className="font-medium">{field.field_label}</span>
                        <span className="text-xs text-slate-500 ml-2">({field.field_name})</span>
                        <span className={`text-xs ml-2 px-2 py-0.5 rounded ${field.field_type === 'text' ? 'bg-blue-100 text-blue-700' : field.field_type === 'select' ? 'bg-purple-100 text-purple-700' : field.field_type === 'date' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}`}>{field.field_type}</span>
                        {field.required && <span className="text-xs ml-2 px-2 py-0.5 rounded bg-red-100 text-red-700">Required</span>}
                        <span className="text-xs text-slate-400 ml-2">({field.applies_to?.join(', ') || 'all'})</span>
                      </div>
                      <button onClick={() => deleteField(field.id)} className="text-red-500 hover:text-red-700"><Trash2 size={18} /></button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-slate-500 text-center py-4">No custom fields yet. Add fields to customize your Lead, Prospect, and Customer forms.</p>}
      </div>
    </div>
  );
};

// Invoices Tab Component

export default FieldManager;
